import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import useLoginAndSubscriptionGuide from '@/lib/hooks/common/useLoginAndSubscriptionGuide';
import { cn } from '@/lib/utils';
import { InsMonitorModel } from '@/types/home';
import LockFilled from '@/icons/lock-filled.svg';
import UnlockFilled from '@/icons/unlock-filled.svg';
import { useUserProvider } from '@/context/UserProvider';
import { ReactNode, useMemo } from 'react';
import { Button } from 'antd';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';

const VARIANT_MAP = {
  default: 'default',
  button: 'button',
} as const;

type LockedWrapperVariant = (typeof VARIANT_MAP)[keyof typeof VARIANT_MAP];

interface LockedWrapperProps {
  className?: string;
  children: React.ReactNode;
  locked?: boolean;
  mini?: boolean;
  blur?: number;
  /** 需要解锁的账号，如果为空，则使用全局用户上下文中的 currentLockedAccount */
  account?: InsMonitorModel;
  variant?: LockedWrapperVariant;
  /** 解锁提示 */
  tips?: string;
  /** 点击解锁 */
  onClick?: () => void;
  tabFunc?: 'story' | 'highlights' | 'post';
}

/**
 * 锁定包装器组件
 * @description 为内容添加模糊效果和锁定状态提示。当 locked=false 时直接返回子组件,不添加包装效果
 * @param className
 * @param {React.ReactNode} children - 需要被包装的子组件
 * @param {boolean} locked - 是否处于锁定状态,当为false时直接返回children
 * @param mini
 * @param {number} blur - 模糊效果的像素值,默认为20px
 * @param {InsMonitorModel} account - 需要解锁的账号，如果为空，则使用全局用户上下文中的 currentLockedAccount
 * @param {string} tips - 解锁提示，如果为空，则使用默认提示
 * @param variant
 * @param {() => void} onClick - 点击解锁的回调函数
 * @param tabFunc
 * @returns {JSX.Element} 当locked=true时返回添加模糊效果和锁定提示的组件,否则直接返回children
 */
const LockedWrapper = ({
  className,
  children,
  locked,
  mini = false,
  blur = 10,
  account,
  tips,
  variant = VARIANT_MAP.default,
  onClick,
  tabFunc,
}: LockedWrapperProps): ReactNode => {
  const { t } = useTranslation();
  const { isLogin } = useUserProvider();
  const { handleLoginAndSubscriptionGuide } = useLoginAndSubscriptionGuide({ account });

  const variantInfo = useMemo(() => {
    if (variant === VARIANT_MAP.default) {
      return {
        title: tips || t('Every detail matters—unlock everything now!'),
      };
    }
    if (variant === VARIANT_MAP.button) {
      return {
        title: isLogin
          ? t('You can view for free. Want it faster? Go Pro.')
          : t('Sign up for free to unlock full Instagram content.'),
        buttonText: isLogin ? t('Upgrade to Premium') : t('Sign Up to Unlock'),
      };
    }
  }, [variant, tips, isLogin]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      handleLoginAndSubscriptionGuide();
    }
  };

  if (!locked) {
    return children;
  }

  const viewForFreeText = () => {
    if (tabFunc === 'highlights') {
      return t('🎁 View Highlights for Free');
    }
    if (tabFunc === 'post') {
      return t('🎁 View Post for Free');
    }
    return t('🎁 View Stories for Free');
  };

  if (mini) {
    return (
      <div className={cn('relative', className)}>
        <div className="select-none" style={{ filter: `blur(${blur}px)` }}>
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center" onClick={handleClick}>
          <div className="flex items-center justify-center gap-6 cursor-pointer select-none">
            <LockFilled />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <div className="select-none" style={{ filter: `blur(${blur}px)` }}>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        {variant === VARIANT_MAP.default && (
          <div
            className="flex items-center justify-center gap-6 w-[280px] px-6 py-3 bg-[#0C0C0C] rounded-[0.375rem] cursor-pointer select-none hover:bg-black"
            onClick={handleClick}
          >
            <Image src="/images/report/not-active/data.webp" alt="locked" width={20} height={20} />
            <div className={cn('text-white text-[0.875rem] leading-[1.125rem] tracking-[1px] ')}>
              {variantInfo?.title}
            </div>
          </div>
        )}
        {variant === VARIANT_MAP.button && (
          <div className="flex flex-col items-center justify-center gap-5 h-[220px] px-5 bg-black/80 rounded-xl">
            <div className="flex flex-col items-center justify-center gap-5">
              <Button
                type="primary"
                className="w-[200px] h-[38px] text-[0.875rem] leading-5"
                icon={null}
                onClick={() => {
                  ga_logEvent(EventName.view_stories_for_free);
                  const dom = document.getElementById('free-tools-title');
                  if (dom) {
                    dom.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {viewForFreeText()}
              </Button>
              <Button
                type="primary"
                className="w-[200px] h-[38px] text-[0.875rem] leading-5"
                icon={<UnlockFilled />}
                onClick={handleClick}
              >
                {variantInfo?.buttonText}
              </Button>
            </div>
            <div className={cn('text-white text-[0.875rem] leading-5 tracking-[1px] text-center')}>
              {variantInfo?.title}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LockedWrapper;
